import type {
  AdminUserDetailResponse,
  AdminUserListResponse,
  AdminUserRole,
  AdminUserStats,
  UserBetHistoryQuery,
  UserBetHistoryResponse,
} from "../../../types/api.generated";

export type {
  AdminUserDetailResponse,
  AdminUserListItem,
  AdminUserListResponse,
  AdminUserRole,
  AdminUserStats,
  UserBetHistoryQuery,
  UserBetHistoryResponse,
} from "../../../types/api.generated";

export type AdminUserSortProperty =
  | "username"
  | "email"
  | "totalBets"
  | "pointsBalance"
  | "netResult";

export interface AdminUserQueryParams {
  page?: number;
  limit?: number;
  sort?: AdminUserSortProperty;
  order?: "ASC" | "DESC";
  query?: string;
}

const getAuthHeaders = () => {
  const token = sessionStorage.getItem("tokenUser");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
};

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || "";

const buildUrl = (path: string, params?: URLSearchParams) => {
  const base = getBaseUrl();
  const search = params?.toString();
  const suffix = search ? `${path}?${search}` : path;
  return base ? `${base}${suffix}` : suffix;
};

const assertOk = async (response: Response, label: string) => {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`${label}: ${response.status} ${response.statusText} ${text}`);
  }
};

export const getAdminUserStats = async (): Promise<AdminUserStats> => {
  const response = await fetch(buildUrl("/admin/users/stat"), {
    headers: getAuthHeaders(),
  });
  await assertOk(response, "Failed to fetch user statistics");
  return response.json();
};

export const getAdminUsers = async (params: AdminUserQueryParams = {}): Promise<AdminUserListResponse> => {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set("page", String(params.page));
  if (params.limit) searchParams.set("limit", String(params.limit));
  if (params.sort) searchParams.set("sort", params.sort);
  if (params.order) searchParams.set("order", params.order);
  const query = params.query?.trim();
  if (query) searchParams.set("query", query);

  const response = await fetch(buildUrl("/admin/users", searchParams), {
    headers: getAuthHeaders(),
  });
  await assertOk(response, "Failed to fetch users");
  return response.json();
};

export const getAdminUser = async (userId: string): Promise<AdminUserDetailResponse> => {
  const response = await fetch(buildUrl(`/admin/users/${userId}`), {
    headers: getAuthHeaders(),
  });
  await assertOk(response, "Failed to fetch user detail");
  return response.json();
};

export const updateAdminUserRole = async (
  userId: string,
  role: AdminUserRole,
): Promise<AdminUserDetailResponse> => {
  const response = await fetch(buildUrl(`/admin/users/${userId}/role`), {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ role }),
  });
  await assertOk(response, "Failed to update user role");
  return response.json();
};

export const blockAdminUser = async (userId: string): Promise<AdminUserDetailResponse> => {
  const response = await fetch(buildUrl(`/admin/users/${userId}/disabled`), {
    method: "POST",
    headers: getAuthHeaders(),
  });
  await assertOk(response, "Failed to disable user");
  return response.json();
};

export const activateAdminUser = async (userId: string): Promise<AdminUserDetailResponse> => {
  const response = await fetch(buildUrl(`/admin/users/${userId}/enabled`), {
    method: "POST",
    headers: getAuthHeaders(),
  });
  await assertOk(response, "Failed to activate user");
  return response.json();
};

export const getAdminUserBets = async (
  userId: string,
  params: UserBetHistoryQuery = {},
): Promise<UserBetHistoryResponse> => {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set("page", String(params.page));
  if (params.limit) searchParams.set("limit", String(params.limit));

  const response = await fetch(buildUrl(`/users/${userId}/bets`, searchParams), {
    headers: getAuthHeaders(),
  });
  await assertOk(response, "Failed to fetch user bets");
  return response.json();
};
